<script setup>
import Button from "primevue/button";
import { computed } from "vue";
import { trans } from 'laravel-vue-i18n';

const props = defineProps({
    item: Object,
})

const emit = defineEmits(['remove', 'edit']);

const unitLabels = {
    hour: trans('rental.card_per_hour'),
    minute: trans('rental.card_per_minute'),
    day: trans('rental.card_per_day'),
    weekend: trans('rental.card_per_weekend'),
    week: trans('rental.card_per_week'),
    month: trans('rental.card_per_month'),
    year: trans('rental.card_per_year'),
}

function formatPrice(value, currency) {
    return new Intl.NumberFormat('cs-CZ', {
        style: 'currency',
        currency: currency || 'CZK',
        minimumFractionDigits: 0,
    }).format(value)
}

const visibleAttributes = computed(() =>
    (props.item.attributes ?? []).filter(a => a.is_visible)
)

const mainImage = computed(() => {
    const first = props.item.gallery?.[0]
    if (!first) return null
    return first?.full_url ?? first
})
</script>

<template>
    <div v-if="item" class="border dark:border-neutral-700 rounded-xl overflow-hidden bg-white dark:bg-neutral-900 flex flex-col hover:shadow-md transition-shadow">

        <!-- Image / placeholder -->
        <div class="h-32 relative overflow-hidden bg-neutral-100 dark:bg-neutral-800">
            <img v-if="mainImage" :src="mainImage" alt="" class="w-full h-full object-cover"/>
            <div v-else class="w-full h-full flex items-center justify-center">
                <i :class="item.icon ?? item.type ?? 'pi pi-box'" class="!text-4xl text-neutral-300 dark:text-neutral-600"/>
            </div>
        </div>

        <!-- Body -->
        <div class="p-4 flex flex-col gap-2 grow">
            <!-- Name -->
            <div class="flex items-center gap-2">
                <i :class="item.icon ?? item.type ?? 'pi pi-box'" class="text-roori-blue shrink-0 text-sm"/>
                <h3 class="font-semibold text-gray-900 dark:text-white truncate">{{ item.name }}</h3>
            </div>

            <!-- Price -->
            <p class="text-lg font-bold text-gray-800 dark:text-gray-100">
                {{ formatPrice(item.unit_price, item.currency) }}
                <span class="text-sm font-normal text-gray-400 dark:text-gray-500">
                    / {{ unitLabels[item.unit] ?? item.unit }}
                </span>
            </p>

            <!-- Attributes -->
            <div v-if="visibleAttributes.length > 0" class="flex flex-wrap gap-1.5 mt-1">
                <span
                    v-for="attr in visibleAttributes.slice(0, 3)"
                    :key="attr.label"
                    class="inline-flex items-center gap-1 text-xs bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-300 px-2 py-1 rounded-full"
                >
                    <i :class="attr.icon" class="text-[10px]"/>
                    {{ attr.label }}: <span class="font-semibold">{{ attr.value }}</span>
                </span>
                <span v-if="visibleAttributes.length > 3"
                      class="inline-flex items-center text-xs bg-neutral-100 dark:bg-neutral-800 text-neutral-400 px-2 py-1 rounded-full">
                    +{{ visibleAttributes.length - 3 }}
                </span>
            </div>
        </div>

        <!-- Footer actions -->
        <div class="border-t dark:border-neutral-700 px-4 py-2 flex items-center justify-between bg-neutral-50 dark:bg-neutral-950">
            <Button :label="$t('common.edit')" icon="pi pi-pencil" text severity="secondary" size="small" @click="emit('edit')"/>
            <Button icon="pi pi-trash" text severity="danger" rounded size="small" @click="emit('remove')"/>
        </div>
    </div>
</template>

<style scoped lang="scss">
</style>
